import { useEffect, useRef, useState } from 'react';
import {
  getBookingAttachmentPreviews,
  uploadBookingAttachment,
} from '../../lib/customer-api';

const MAX_PHOTOS = 5;

/**
 * Reference-photo picker for the "Your expectations concerning the pro"
 * step. Uploads each image to S3 via the anonymous booking-attachment
 * route (works for logged-out customers mid-flow) and hands the parent an
 * array of public URLs to stash in the booking draft. Shared by the
 * session (cleaning / childcare) and treatment (hair / beauty) flows.
 */
export default function ExpectationPhotos({
  value,
  onChange,
}: {
  value: string[];
  onChange: (urls: string[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The upload returns the canonical S3 URL (private bucket) — which won't
  // render in an <img> on its own, so the thumbnail showed broken. Keep a
  // local blob object URL per uploaded file for an instant, reliable
  // preview; the canonical URL is still what we persist to the draft and
  // attach at checkout (the pro sees a server-signed version later).
  const [previewByUrl, setPreviewByUrl] = useState<Record<string, string>>({});

  // Revoke any blob URLs we created when the component unmounts.
  useEffect(() => {
    return () => {
      for (const blob of Object.values(previewByUrl)) {
        if (blob.startsWith('blob:')) URL.revokeObjectURL(blob);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On (re)mount or when persisted photos arrive without a live blob
  // preview — e.g. the customer navigated back to this step — fetch a
  // short-lived signed URL for each so the thumbnails render instead of
  // showing the private bucket's broken canonical URL. Best-effort.
  useEffect(() => {
    const missing = value.filter((u) => !previewByUrl[u]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const signed = await getBookingAttachmentPreviews(missing);
        if (!cancelled && Object.keys(signed).length) {
          // Existing entries (live blobs) win over fetched signed URLs.
          setPreviewByUrl((prev) => ({ ...signed, ...prev }));
        }
      } catch {
        // Leave the canonical URL as the fallback src.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const room = MAX_PHOTOS - value.length;
    if (room <= 0) return;
    const picked = Array.from(files).slice(0, room);
    setUploading(true);
    setError(null);
    try {
      const uploaded: string[] = [];
      const newPreviews: Record<string, string> = {};
      for (const f of picked) {
        const res = await uploadBookingAttachment(f);
        if (res?.url) {
          uploaded.push(res.url);
          newPreviews[res.url] = URL.createObjectURL(f);
        }
      }
      if (uploaded.length) {
        setPreviewByUrl((prev) => ({ ...prev, ...newPreviews }));
        onChange([...value, ...uploaded]);
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          'Could not upload that image. Please try a smaller JPEG/PNG.',
      );
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removeAt = (i: number) => {
    const url = value[i];
    const blob = previewByUrl[url];
    if (blob) {
      URL.revokeObjectURL(blob);
      setPreviewByUrl((prev) => {
        const next = { ...prev };
        delete next[url];
        return next;
      });
    }
    onChange(value.filter((_, idx) => idx !== i));
  };

  return (
    <div className="mt-4">
      <p className="font-['DM_Sans',sans-serif] text-[13px] text-[#3a3335] mb-2">
        Add reference photos{' '}
        <span className="text-[#908e8e]">(optional)</span>
      </p>
      <div className="flex flex-wrap gap-2">
        {value.map((url, i) => (
          <div
            key={url}
            className="relative w-[72px] h-[72px] rounded-lg overflow-hidden border border-[#eee] bg-[#faf7f8]"
          >
            <img
              src={previewByUrl[url] ?? url}
              alt=""
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={() => removeAt(i)}
              aria-label="Remove photo"
              className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/55 text-white text-xs leading-none flex items-center justify-center hover:bg-black/75"
            >
              ×
            </button>
          </div>
        ))}
        {value.length < MAX_PHOTOS && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="w-[72px] h-[72px] rounded-lg border border-dashed border-[#c9bfc1] text-[#8e2a2f] flex flex-col items-center justify-center gap-0.5 hover:border-[#8e2a2f] disabled:opacity-50 transition-all"
          >
            <span className="text-lg leading-none">
              {uploading ? '…' : '+'}
            </span>
            <span className="font-['DM_Sans',sans-serif] text-[10px] leading-none">
              {uploading ? 'Uploading' : 'Photo'}
            </span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <p className="font-['DM_Sans',sans-serif] text-[11px] text-[#908e8e] mt-2">
        Up to {MAX_PHOTOS} photos · JPEG, PNG or WebP. The pro will see these
        with your notes.
      </p>
      {error ? (
        <p className="font-['DM_Sans',sans-serif] text-[11px] text-red-600 mt-1">
          {error}
        </p>
      ) : null}
    </div>
  );
}
